import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DROPDOWN_LABELS } from '../constants';
import { AiClipPromptOption } from '../types';
import { useAiSearchApi } from './useAiSearchApi';
import useDialog from '@/components/organisms/useDialog';

export function useAiSearchActions() {
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState('');
  const [selectedPromptOption, setSelectedPromptOption] = useState<AiClipPromptOption | null>(DROPDOWN_LABELS[0]);
  const [arePromptOptionsVisible, setArePromptOptionsVisible] = useState(false);
  const [isPromptOptionSelected, setIsPromptOptionSelected] = useState(true);
  const [isRecentSearchesVisible, setIsRecentSearchesVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedSearchToDelete, setSavedSearchToDelete] = useState<any>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Dialog states
  const {
    isOpen: isNoResultsDialogOpen,
    closeDialog: closeNoResultsDialog,
    openDialog: openNoResultsDialog
  } = useDialog();

  const { isOpen: isLoaderDialogOpen, closeDialog: closeLoaderDialog, openDialog: openLoaderDialog } = useDialog();

  const {
    isOpen: isSourceSelectionDialogOpen,
    closeDialog: closeSourceSelectionDialog,
    openDialog: openSourceSelectionDialog
  } = useDialog();

  const {
    isOpen: isDeleteConfirmationOpen,
    openDialog: openDeleteConfirmationDialog,
    closeDialog: closeDeleteConfirmationDialog
  } = useDialog();

  // API hook
  const {
    savedSearches,
    loadingRelevantSources,
    relevantSourceList,
    loadSavedSearches,
    deleteSearch,
    getRelevantSearchSources,
    createAiSearch
  } = useAiSearchApi();

  useEffect(() => {
    loadSavedSearches();
  }, [loadSavedSearches]);

  // Computed values
  const isSearchButtonDisabled = useMemo(() => {
    return !prompt.trim() || isLoading;
  }, [prompt, isLoading]);

  // Form actions
  const showPromptOptions = useCallback(() => {
    if (selectedPromptOption) {
      return;
    }
    setArePromptOptionsVisible(true);
  }, [selectedPromptOption]);

  const togglePopover = useCallback(() => {
    if (isLoading) {
      return;
    }
    setArePromptOptionsVisible(val => !val);
  }, [isLoading]);

  const toggleRecentSearches = useCallback(() => {
    setIsRecentSearchesVisible(prev => {
      const newValue = !prev;
      // If opening recent searches, close prompt options
      if (!prev && newValue) {
        setArePromptOptionsVisible(false);
      }
      return newValue;
    });
  }, []);

  const closeRecentSearches = useCallback(() => {
    setIsRecentSearchesVisible(false);
  }, []);

  const closePromptOptions = useCallback(() => {
    setArePromptOptionsVisible(false);
  }, []);

  const selectPromptOption = useCallback((option: AiClipPromptOption) => {
    setSelectedPromptOption(option);
    setIsPromptOptionSelected(true);
    setArePromptOptionsVisible(false);
  }, []);

  const clearPrompt = useCallback(() => {
    setIsPromptOptionSelected(false);
  }, []);

  const clearPromptOption = useCallback(() => {
    setSelectedPromptOption(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setPrompt('');
  }, []);

  const resetInputForm = useCallback(() => {
    closeNoResultsDialog();
    clearPromptOption();
    clearPrompt();
    setIsLoading(false);
  }, [closeNoResultsDialog, clearPromptOption, clearPrompt]);

  const handleSearchSelect = useCallback(
    (searchId: string) => {
      navigate(`/search/${searchId}/clips`);
    },
    [navigate]
  );

  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value);
  }, []);

  // Focus management
  useEffect(() => {
    if (selectedPromptOption) {
      inputRef.current?.focus();
    }
  }, [selectedPromptOption]);

  // Search actions
  const handleRelevantSourceGeneration = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!selectedPromptOption) {
        selectPromptOption(DROPDOWN_LABELS[0]);
        return;
      }

      try {
        openSourceSelectionDialog();
        await getRelevantSearchSources(prompt, selectedPromptOption);
      } catch (err) {
        if (err instanceof Error && err.message === 'NO_RESULTS') {
          openNoResultsDialog();
        }
        closeSourceSelectionDialog();
      }
    },
    [
      selectedPromptOption,
      prompt,
      selectPromptOption,
      getRelevantSearchSources,
      openSourceSelectionDialog,
      openNoResultsDialog,
      closeSourceSelectionDialog
    ]
  );

  const handleSelectedSource = useCallback(
    async (selectedSource: string[]) => {
      try {
        closeSourceSelectionDialog();
        setIsLoading(true);
        openLoaderDialog();

        await createAiSearch(prompt, selectedPromptOption!, selectedSource);
      } catch (err) {
        if (err instanceof Error && err.message === 'NO_RESULTS') {
          openNoResultsDialog();
        }
      } finally {
        setIsLoading(false);
        closeLoaderDialog();
      }
    },
    [
      prompt,
      selectedPromptOption,
      createAiSearch,
      closeSourceSelectionDialog,
      openLoaderDialog,
      openNoResultsDialog,
      closeLoaderDialog
    ]
  );

  // Saved search actions
  const handleDeleteClick = useCallback(
    (savedSearch: any) => {
      setSavedSearchToDelete(savedSearch);
      openDeleteConfirmationDialog();
    },
    [openDeleteConfirmationDialog]
  );

  const deleteSavedSearch = useCallback(async () => {
    if (!savedSearchToDelete) {
      return;
    }

    const success = await deleteSearch(savedSearchToDelete.id);
    if (success) {
      closeDeleteConfirmationDialog();
      setSavedSearchToDelete(null);
    }
  }, [savedSearchToDelete, deleteSearch, closeDeleteConfirmationDialog]);

  // Navigation actions
  const triggerUploadContent = useCallback(() => {
    resetInputForm();
    navigate('/import');
  }, [navigate, resetInputForm]);

  return {
    // Form state
    prompt,
    selectedPromptOption,
    arePromptOptionsVisible,
    isPromptOptionSelected,
    isRecentSearchesVisible,
    isLoading,
    isSearchButtonDisabled,
    inputRef,

    // Dialog states
    isNoResultsDialogOpen,
    isLoaderDialogOpen,
    isSourceSelectionDialogOpen,
    isDeleteConfirmationOpen,

    // Dialog actions
    closeNoResultsDialog,
    closeLoaderDialog,
    closeSourceSelectionDialog,
    closeDeleteConfirmationDialog,

    // Form actions
    setPrompt: handlePromptChange,
    showPromptOptions,
    togglePopover,
    toggleRecentSearches,
    closeRecentSearches,
    closePromptOptions,
    selectPromptOption,
    clearPromptOption,
    handleSearchSelect,

    // Search actions
    handleRelevantSourceGeneration,
    handleSelectedSource,

    // Saved search actions
    handleDeleteClick,
    deleteSavedSearch,

    // Navigation actions
    resetInputForm,
    triggerUploadContent,

    // API data
    savedSearches: savedSearches,
    loadingRelevantSources: loadingRelevantSources,
    relevantSourceList: relevantSourceList
  };
}
